<div class="machine-details-step">
  <div class="row">
    <div class="col-12">
      <h4 class="mb-4" *ngIf="showAnalysisResult">Machine Information</h4>

      <!-- Step 1: Image Upload or Manual Entry Choice -->
      <div class="form-section mb-4" *ngIf="!analyzingImage && !showAnalysisResult && !showConfirmationSteps && !showFinalConfirmation">
        <div class="text-center mb-4">
          <div class="rubbl-ai-logo mb-3">
            <img src="https://company.rubbl.com/pubweb/marketplace/images/icons/rubbl-ai-2.png" alt="Rubbl AI" class="ai-logo" />
          </div>
          <p class="text-muted">
            One photo of your machine or serial number plate is all it takes, we'll identify your machine and recommend competitive rental pricing in seconds.
          </p>
        </div>

        <div class="row justify-content-center">
          <div class="col-md-8">
            <common-drag-drop-file-upload
              acceptedFileTypes=".png, .jpg, .jpeg, .webp"
              dropPrompt="Open camera or browse for picture"
              dropZoneIconClass="icon-camera"
              [showAcceptedFileTypes]="false"
              (fileChange)="onImageFileSelected($event)"
            ></common-drag-drop-file-upload>
          </div>
        </div>

        <div class="text-center mt-4">
          <button type="button" class="btn btn-outline-secondary" (click)="skipImageUpload()"><i class="icon-edit mr-2"></i>Skip and fill manually</button>

          <div class="mt-3">
            <button type="button" class="btn btn-link text-muted" (click)="onCancel()">
              <i class="icon-arrow-left mr-1"></i>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Step 2: AI Analysis Loading -->
      <div class="mb-4 text-center py-5" *ngIf="analyzingImage">
        <div class="mb-3">
          <img src="https://company.rubbl.com/pubweb/marketplace/images/icons/rubbl-ai-2.png" alt="Rubbl AI" class="ai-logo-loading" />
        </div>
        <h5 class="mb-2 d-flex align-items-center justify-content-center">
          <common-spinner-loader class="mr-2"></common-spinner-loader>Determining your machine
        </h5>
        <p class="text-muted font-weight-light">This usually takes a few seconds...</p>
      </div>

      <!-- Step 3: Confirmation Steps -->
      <div class="mb-4" *ngIf="showConfirmationSteps">
        <div class="py-4">
          <div class="mb-3 text-center">
            <img src="https://company.rubbl.com/pubweb/marketplace/images/icons/rubbl-ai-2.png" alt="Rubbl AI" class="ai-logo" />
          </div>

          <div class="mb-4">
            <h4 class="mb-4">{{ getCurrentStepQuestion() }}</h4>

            <!-- Subtext for listing options -->
            <p class="text-muted mb-4" *ngIf="getCurrentStepType() === 'listingOptions'">You will be able to change these options later.</p>

            <!-- Yes/No buttons for AI-provided data -->
            <div class="text-center" *ngIf="!showDropdown && !getCurrentStepType().includes('-manual') && !isSpecialQuestionType()">
              <button type="button" class="btn btn-primary btn-lg mr-md-3 mb-3 mb-md-0" (click)="confirmCurrentStep()" [disabled]="!canConfirmCurrentStep()">
                <i class="icon-check mr-2"></i>Yes
              </button>
              <button type="button" class="btn btn-outline-secondary btn-lg" (click)="rejectCurrentStep()"><i class="icon-times mr-2"></i>No</button>
            </div>

            <!-- Dropdown for corrections -->
            <div class="mx-auto" style="max-width: 400px" *ngIf="shouldShowDropdown()">
              <div class="form-group">
                <label class="font-weight-bold text-dark mb-2">Please select the correct {{ getCurrentStepType() }}:</label>

                <!-- Show dropdown when not in manual input mode -->
                <ng-container *ngIf="!showManualInput">
                  <select class="form-control rounded mb-2" [(ngModel)]="selectedCorrectionValue">
                    <option value="">Select {{ getCurrentStepType() }}</option>
                    <option *ngFor="let option of currentDropdownOptions" [value]="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 mb-2 d-block"
                    *ngIf="allowManualEntryForMachineTypeStep()"
                    (click)="showManualInputForDropdown()"
                  >
                    <i class="icon-edit mr-1"></i>{{ getManualEntryLinkText() }}
                  </button>
                </ng-container>

                <!-- Show manual input when "Enter your own value" is clicked -->
                <ng-container *ngIf="showManualInput && allowManualEntryForMachineTypeStep()">
                  <input
                    [type]="getCurrentStepType() === 'year' ? 'number' : 'text'"
                    class="form-control form-control-lg rounded mb-2"
                    [(ngModel)]="selectedCorrectionValue"
                    (ngModelChange)="saveValueToConfirmedData($event)"
                    [placeholder]="'Enter ' + getCurrentStepType()"
                  />
                  <button type="button" class="btn btn-link btn-sm p-0 mb-2 d-block" (click)="hideManualInputForDropdown()">
                    <i class="icon-arrow-left mr-1"></i>Back to selection
                  </button>
                </ng-container>
              </div>
              <div class="text-center">
                <button type="button" class="btn btn-success rounded" (click)="saveCorrection(selectedCorrectionValue)" [disabled]="!selectedCorrectionValue">
                  Save
                </button>
              </div>
            </div>

            <!-- Manual input for missing data -->
            <div
              class="mx-auto"
              style="max-width: 400px"
              *ngIf="getCurrentStepType().includes('-manual') || getCurrentStepType() === 'hours' || getCurrentStepType() === 'serialNumber'"
            >
              <!-- Dropdown for type-manual, make-manual, model-manual, year-manual with manual input button -->
              <div
                class="form-group"
                *ngIf="
                  getCurrentStepType() === 'type-manual' ||
                  getCurrentStepType() === 'make-manual' ||
                  getCurrentStepType() === 'model-manual' ||
                  getCurrentStepType() === 'year-manual'
                "
              >
                <label class="font-weight-bold text-dark mb-2" *ngIf="getCurrentStepType() !== 'year-manual'">{{ getInputLabel() }}</label>

                <!-- Show dropdown initially or when manual input not selected -->
                <ng-container *ngIf="!showManualInput">
                  <select
                    class="form-control rounded mb-2"
                    [(ngModel)]="selectedCorrectionValue"
                    (ngModelChange)="onDropdownChange($event)"
                  >
                    <option value="">Select {{ getCurrentStepType().replace("-manual", "") }}</option>
                    <option *ngFor="let option of currentDropdownOptions" [value]="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 mb-2 d-block"
                    *ngIf="allowManualEntryForMachineTypeStep()"
                    (click)="showManualInputForDropdown()"
                  >
                    <i class="icon-edit mr-1"></i>{{ getManualEntryLinkText() }}
                  </button>
                </ng-container>

                <!-- Show text input when "Enter your own value" is clicked -->
                <ng-container *ngIf="showManualInput && allowManualEntryForMachineTypeStep()">
                  <input
                    [type]="getCurrentStepType() === 'year-manual' ? 'number' : 'text'"
                    class="form-control form-control-lg rounded mb-2"
                    [(ngModel)]="selectedCorrectionValue"
                    (ngModelChange)="saveValueToConfirmedData($event)"
                    [placeholder]="'Enter ' + getCurrentStepType().replace('-manual', '')"
                  />
                  <button type="button" class="btn btn-link btn-sm p-0 mb-2 d-block" (click)="hideManualInputForDropdown()">
                    <i class="icon-arrow-left mr-1"></i>Back to selection
                  </button>
                </ng-container>
              </div>

              <!-- Text input for hours and serialNumber -->
              <div class="form-group" *ngIf="getCurrentStepType() === 'hours' || getCurrentStepType() === 'serialNumber'">
                <input
                  [type]="getCurrentStepType() === 'hours' ? 'number' : 'text'"
                  class="form-control form-control-lg rounded mb-3"
                  [(ngModel)]="selectedCorrectionValue"
                  (ngModelChange)="saveValueToConfirmedData($event)"
                  [placeholder]="getInputPlaceholder()"
                />
                <small class="form-text text-muted" *ngIf="getCurrentStepType() === 'serialNumber'"> If you don't have it now we will get it later. </small>
              </div>

              <div class="text-center">
                <button
                  type="button"
                  class="btn btn-success btn-lg rounded"
                  (click)="saveCorrection(selectedCorrectionValue)"
                  [disabled]="getCurrentStepType() !== 'serialNumber' && !selectedCorrectionValue"
                >
                  Next
                </button>
              </div>
            </div>

            <!-- Listing Options -->
            <div class="listing-options" *ngIf="getCurrentStepType() === 'listingOptions'">
              <div class="form-group">
                <common-checkbox-cards
                  [checkboxCards]="checkboxesForRentRtoSellItNow"
                  (checkboxCardsChange)="onListingOptionsChange($event)"
                ></common-checkbox-cards>
              </div>
              <div class="text-center">
                <button type="button" class="btn btn-success btn-lg" (click)="saveListingOptions()" [disabled]="!canProceedWithListingOptions()">Next</button>
              </div>
            </div>

            <!-- Location -->
            <div class="location-question" *ngIf="getCurrentStepType() === 'location'">
              <div class="form-group">
                <!-- Show checkbox cards if there are any locations -->
                <div *ngIf="locationCheckboxCards?.length > 0 && !showLocationTypeahead">
                  <common-checkbox-cards
                    [checkboxCards]="locationCheckboxCards"
                    [singleSelect]="true"
                    (checkboxCardsChange)="onLocationCheckboxChange($event)"
                  ></common-checkbox-cards>
                </div>

                <!-- Show location typeahead when "Add new address" is selected or no existing locations -->
                <div *ngIf="showLocationTypeahead || locationCheckboxCards?.length === 0">
                  <common-location-typeahead
                    width="100%"
                    (locationSelected)="locationTypeaheadSelected($event)"
                    placeHolderText="Enter machine location"
                  ></common-location-typeahead>
                </div>
              </div>
              <div class="text-center">
                <button
                  type="button"
                  class="btn btn-success btn-lg"
                  (click)="saveLocation()"
                  [disabled]="!entity.location || !entity.location.address || loading"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm mr-2"></span>
                  {{ loading ? "Creating Location..." : "Next" }}
                </button>
              </div>
            </div>
          </div>

          <!-- Progress indicator -->
          <div class="text-center mt-4 pt-3 border-top">
            <small class="text-muted">Step {{ currentConfirmationStep + 1 }} of {{ confirmationSteps.length }}</small>

            <div class="mt-3" *ngIf="currentConfirmationStep > 0">
              <button type="button" class="btn btn-link text-success" (click)="goBackConfirmationStep()" [disabled]="loading">
                <i class="icon-arrow-left mr-1"></i>
                Previous Step
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Final Confirmation Screen -->
      <div class="form-section mb-4" *ngIf="showFinalConfirmation">
        <div class="final-confirmation">
          <div class="text-center mb-4">
            <h4 class="mb-3">Review Your Machine Details</h4>
          </div>

          <div class="confirmation-summary">
            <p class="lead mb-3 text-center fs-24">
              {{ confirmedData.year }} {{ confirmedData.make }} {{ confirmedData.model }} <br />
              {{ confirmedData.hours }} {{ getUsageSummarySuffix() }} <br />
              {{ confirmedData.location?.address?.city }}, {{ confirmedData.location?.address?.stateProvince }}
            </p>

            <!-- Payment Method Section -->
            <div class="form-section mb-4" *ngIf="!account?.bypassCreditCard">
              <h5 class="card-title align-items-center d-flex">Payment method on file</h5>
              <p class="font-weight-light">
                To ensure the credibility and reputation of the Rubbl platform, the Owner is required to provide a valid credit card on file when listing a
                machine.
              </p>
              <common-select-payment-methods (paymentMethodIdChange)="paymentMethodIdChange($event)"></common-select-payment-methods>
            </div>

            <!-- Terms and Conditions -->
            <div class="form-section mb-4 mt-5">
              <h5 class="section-header">Terms and Conditions</h5>
              <div class="form-group mt-3" *ngFor="let term of terms; let i = index">
                <div class="d-flex align-items-start">
                  <app-input-checkbox [label]="term.label" [(checked)]="term.checked" [large]="true" [invalid]="false" [useHtml]="true"></app-input-checkbox>
                  <app-questionmark-tooltip *ngIf="term.tooltip" class="ml-2" [displayLearnMore]="false" [text]="term.tooltip" mobilePlacement="left">
                  </app-questionmark-tooltip>
                </div>
              </div>
            </div>

            <div class="text-center mt-4">
              <button type="button" class="btn btn-success btn-lg" (click)="createMachineFromConfirmedData()" [disabled]="loading || !canCreateMachine()">
                <span *ngIf="loading" class="spinner-border spinner-border-sm mr-2"></span>
                {{ loading ? "Loading..." : "Next" }}
              </button>

              <div class="mt-3">
                <button type="button" class="btn btn-link text-success" (click)="goBackFromFinalConfirmation()" [disabled]="loading">
                  <i class="icon-arrow-left mr-1"></i>
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Main Form (shown after confirmation steps) -->
        <div *ngIf="showMainForm">
          <!-- Machine Type, Make, Model Selection -->
          <div class="form-section mb-4">
            <h5 class="section-header">Machine Details</h5>

            <!-- Machine Type Dropdown -->
            <div class="form-group">
              <label for="machineType">Machine Type *</label>
              <select
                id="machineType"
                class="form-control"
                [(ngModel)]="selectedMachineType"
                (change)="onMachineTypeChange($event.target.value)"
                [disabled]="loading"
              >
                <option value="">Select Machine Type</option>
                <option *ngFor="let type of machineTypes" [value]="type.value">
                  {{ type.label }}
                </option>
              </select>
              <small class="form-text text-muted">Select the type of machine you're listing</small>
            </div>

            <!-- Machine Make Dropdown -->
            <div class="form-group" *ngIf="selectedMachineType">
              <label for="machineMake">Make *</label>
              <select
                id="machineMake"
                class="form-control"
                [(ngModel)]="selectedMachineMake"
                (change)="onMachineMakeChange($event.target.value)"
                [disabled]="loading || !makes.length"
              >
                <option value="">Select Make</option>
                <option *ngFor="let make of makes" [value]="make.value">
                  {{ make.label }}
                </option>
              </select>
            </div>

            <!-- Machine Model Dropdown -->
            <div class="form-group" *ngIf="selectedMachineMake">
              <label for="machineModel">Model *</label>
              <select
                id="machineModel"
                class="form-control"
                [(ngModel)]="selectedMachineModel"
                (change)="onMachineModelChange($event.target.value)"
                [disabled]="loading || !models.length"
              >
                <option value="">Select Model</option>
                <option *ngFor="let model of models" [value]="model.value">
                  {{ model.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Listing Options -->
          <div class="form-section mb-4">
            <h5 class="section-header">Listing Options</h5>
            <div class="form-group">
              <common-checkbox-cards
                [checkboxCards]="[
              { value: 'rent', label: 'Rent', checked: isRentSelected },
              { value: 'rpo', label: 'Rent To Own', checked: entity.rpoEnabled, disabled: !isRentSelected },
              { value: 'buyItNow', label: 'Sell It Now', checked: entity.buyItNowEnabled },
            ]"
                (checkboxCardsChange)="handleCheckboxChange($event)"
              ></common-checkbox-cards>
            </div>
          </div>

          <!-- Rental Rates Section -->
          <div class="form-section mb-4" *ngIf="isRentSelected">
            <h5 class="section-header">Rental Rates</h5>

            <div class="form-group">
              <label for="rentalRateMonthly">Monthly Rental Rate *</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">$</span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  id="rentalRateMonthly"
                  [(ngModel)]="entity.rentalRate"
                  (ngModelChange)="onRentalRateChange($event)"
                  mask="separator"
                  placeholder="e.g. 4,999"
                  [maxLength]="8"
                  thousandSeparator=","
                />
              </div>
              <small class="form-text text-muted" *ngIf="showSuggestedRateText"> This is our suggested rate. Feel free to change it. </small>
            </div>

            <div class="form-group">
              <label for="rentalRateWeekly">Weekly Rental Rate</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">$</span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  id="rentalRateWeekly"
                  [(ngModel)]="entity.rentalRateWeekly"
                  mask="separator"
                  placeholder="e.g. 2,000"
                  [maxLength]="8"
                  thousandSeparator=","
                />
              </div>
            </div>

            <!-- Earnings Display -->
            <div class="alert alert-success" *ngIf="entity.rentalRate || entity.rentalRateWeekly">
              <div class="text-center mb-2"><i class="icon-money mr-2"></i> <strong>Your Earnings</strong></div>
              <div class="row text-center">
                <div class="col-6">
                  <div class="small text-muted">Weekly</div>
                  <div class="h5 text-success mb-0">
                    {{ getWeeklyEarnings() | currency: "USD":"symbol":"1.0-0" }}
                  </div>
                </div>
                <div class="col-6">
                  <div class="small text-muted">Monthly</div>
                  <div class="h5 text-success mb-0">
                    {{ getMonthlyEarnings() | currency: "USD":"symbol":"1.0-0" }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Machine Specifications -->
          <div class="form-section mb-4">
            <h5 class="section-header">Machine Specifications</h5>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label for="year">Year *</label>
                  <select id="year" class="form-control" [(ngModel)]="entity.year" (change)="yearChange($event.target.value)">
                    <option value="">Select Year</option>
                    <option *ngFor="let year of yearDropdownItems" [value]="year.value">
                      {{ year.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6" *ngIf="!isNoneUsage()">
                <div class="form-group">
                  <label for="usageValue">{{ getUsageInputLabel() }} *</label>
                  <input
                    type="number"
                    class="form-control"
                    id="usageValue"
                    [(ngModel)]="entity.usage.standard.value"
                    [placeholder]="isMileageUsage() ? 'e.g. 50000' : 'e.g. 750'"
                  />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="serialNumber">Serial Number</label>
              <input
                type="text"
                class="form-control"
                id="serialNumber"
                [(ngModel)]="entity.serialNumber"
                placeholder="Enter serial number"
                (input)="onSerialNumberInput($event)"
              />
              <small class="form-text text-muted">If you don't have it now we will get it later.</small>
            </div>

            <div class="form-group">
              <label for="description">Description</label>
              <textarea
                class="form-control"
                rows="4"
                [(ngModel)]="entity.description"
                placeholder="Enter optional description about your machine..."
              ></textarea>
            </div>
          </div>

          <!-- Location Section -->
          <div class="form-section mb-4">
            <h5 class="section-header">Location</h5>

            <div class="form-group">
              <label for="location">Machine Location *</label>
              <select id="location" class="form-control" (change)="locationSelected($event.target.value)" *ngIf="yardDropdownItems?.length > 0">
                <option value="">Select Location</option>
                <option value="new">Enter new address</option>
                <option *ngFor="let yard of yardDropdownItems" [value]="yard.label">
                  {{ yard.label }}
                </option>
              </select>

              <div class="mt-2" *ngIf="yardDropdownItems?.length == 0 || showLocationTypeahead">
                <common-location-typeahead
                  width="100%"
                  (locationSelected)="locationTypeaheadSelected($event)"
                  placeHolderText="Enter machine location"
                ></common-location-typeahead>
              </div>
              <small class="form-text text-muted">Your address will not be publicly available.</small>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()" [disabled]="loading">Cancel</button>

            <button type="button" class="btn btn-success btn-lg" (click)="onNext()" [disabled]="!canProceed() || loading">
              <span *ngIf="loading" class="spinner-border spinner-border-sm mr-2"></span>
              {{ loading ? "Creating Machine..." : "Continue to Pricing" }}
            </button>
          </div>

          <!-- Validation Messages -->
          <div class="alert alert-danger mt-3" *ngIf="validationErrors.length > 0">
            <ul class="mb-0">
              <li *ngFor="let error of validationErrors">{{ error }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
